import { useCallback, useRef, useState } from "react";
import {
  cloneTaskEditorState,
  stableHashBlocks,
  threeWayMergeBlocks,
  type ThreeWayMergeResult,
} from "./taskEditorDiff";
import type { TaskBlocksPatch } from "../agent/types";
import type { TaskEditorState } from "./taskEditorUtils";

// agent ↔ user 의 동시 편집을 안전하게 합치기 위한 상태 컨테이너.
//
// 책임:
//  1. canonical state (`currentTask`) 와 revision 을 단일 setter 로 관리
//  2. 턴 시작 시점의 baseline (`turnBaseTask`) 을 기억해서 agent patch 가 도착할 때
//     base / ours / theirs 의 3-way merge 를 수행
//  3. 충돌 (같은 block index 를 user · agent 가 동시 수정) 발생 시 patch 를 보류하고
//     UI 가 "에이전트 적용" / "내 편집 유지" 둘 중 하나를 고르게 함
//
// 의도적으로 *task* (메타 + blocks 전체) 를 단위로 다룬다 — patch 는 blocks 만 변경하므로
// merge 결과는 항상 `{ ...currentTask, blocks: merged }` 형태로 합성된다.

export type AgentPatchOutcome =
  | { kind: "applied" }
  | { kind: "auto_merged" }
  | {
      kind: "conflict";
      reason: "overlap" | "length_mismatch";
      conflictBlockIndexes: number[];
    }
  | { kind: "no_op" }; // currentTask 가 없을 때 (활동지 미선택)

export interface PendingAgentPatch {
  patch: TaskBlocksPatch;
  reason: "overlap" | "length_mismatch";
  conflictBlockIndexes: number[];
}

export interface UseTaskEditorSyncStateResult {
  currentTask: TaskEditorState | undefined;
  revision: string | null;
  pendingAgentPatch: PendingAgentPatch | null;
  setInitial: (state: TaskEditorState | undefined) => void;
  applyUserMutation: (next: TaskEditorState) => void;
  markTurnStart: () => void;
  markTurnEnd: () => void;
  applyAgentPatch: (patch: TaskBlocksPatch) => AgentPatchOutcome;
  resolveConflict: (choice: "accept_agent" | "keep_mine") => void;
  dismissPendingPatch: () => void;
}

interface UseTaskEditorSyncStateArgs {
  // patch 가 (auto-merge 포함) 실제로 적용된 직후 호출. 호출자가 undo 스택 push 등을 처리.
  onAgentApplied?: (nextTask: TaskEditorState) => void;
}

export function useTaskEditorSyncState(
  args: UseTaskEditorSyncStateArgs = {}
): UseTaskEditorSyncStateResult {
  const [currentTask, setCurrentTask] = useState<TaskEditorState | undefined>(
    undefined
  );
  const [pendingAgentPatch, setPendingAgentPatch] =
    useState<PendingAgentPatch | null>(null);

  // ref 로 보관: turn lifecycle 안에서 가장 최신 값을 동기적으로 읽어야 한다
  // (setState 는 다음 렌더 후에야 반영되므로 `applyAgentPatch` 가 같은 turn 내
  // 빠른 user mutation 직후 들어오면 stale 한 currentTask 를 base 로 잡을 위험).
  const currentTaskRef = useRef<TaskEditorState | undefined>(undefined);
  const turnBaseTaskRef = useRef<TaskEditorState | null>(null);
  const onAppliedRef = useRef(args.onAgentApplied);
  onAppliedRef.current = args.onAgentApplied;

  const writeCurrent = useCallback((next: TaskEditorState | undefined) => {
    currentTaskRef.current = next;
    setCurrentTask(next);
  }, []);

  const setInitial = useCallback(
    (state: TaskEditorState | undefined) => {
      writeCurrent(state);
      turnBaseTaskRef.current = null;
      setPendingAgentPatch(null);
    },
    [writeCurrent]
  );

  const applyUserMutation = useCallback(
    (next: TaskEditorState) => {
      writeCurrent(next);
    },
    [writeCurrent]
  );

  const markTurnStart = useCallback(() => {
    const c = currentTaskRef.current;
    turnBaseTaskRef.current = c ? cloneTaskEditorState(c) : null;
  }, []);

  const markTurnEnd = useCallback(() => {
    turnBaseTaskRef.current = null;
  }, []);

  const composeNextTask = useCallback(
    (blocks: TaskEditorState["blocks"]): TaskEditorState | null => {
      const cur = currentTaskRef.current;
      if (!cur) return null;
      return { ...cur, blocks };
    },
    []
  );

  const applyAgentPatch = useCallback(
    (patch: TaskBlocksPatch): AgentPatchOutcome => {
      const cur = currentTaskRef.current;
      if (!cur) return { kind: "no_op" };

      // base 부재 (turn 시작을 못 잡았거나 markTurnEnd 가 이미 호출됨) →
      // 사용자가 안 건드린 것처럼 취급해 그대로 적용. 안전한 fallback.
      const base = turnBaseTaskRef.current ?? cur;
      const merge: ThreeWayMergeResult = threeWayMergeBlocks(
        base.blocks,
        cur.blocks,
        patch.blocks
      );

      if (merge.kind === "applied" || merge.kind === "auto_merged") {
        const next = composeNextTask(merge.blocks);
        if (next) {
          writeCurrent(next);
          onAppliedRef.current?.(next);
        }
        return { kind: merge.kind };
      }

      // conflict — patch 보류, UI 에게 결정권 위임
      setPendingAgentPatch({
        patch,
        reason: merge.reason,
        conflictBlockIndexes: merge.conflictBlockIndexes,
      });
      return {
        kind: "conflict",
        reason: merge.reason,
        conflictBlockIndexes: merge.conflictBlockIndexes,
      };
    },
    [composeNextTask, writeCurrent]
  );

  const resolveConflict = useCallback(
    (choice: "accept_agent" | "keep_mine") => {
      setPendingAgentPatch((pending) => {
        if (!pending) return null;
        if (choice === "accept_agent") {
          const next = composeNextTask(pending.patch.blocks);
          if (next) {
            writeCurrent(next);
            onAppliedRef.current?.(next);
          }
        }
        // "keep_mine" 은 currentTask 그대로 두고 patch 만 폐기.
        return null;
      });
    },
    [composeNextTask, writeCurrent]
  );

  const dismissPendingPatch = useCallback(() => {
    setPendingAgentPatch(null);
  }, []);

  const revision = currentTask ? stableHashBlocks(currentTask.blocks) : null;

  return {
    currentTask,
    revision,
    pendingAgentPatch,
    setInitial,
    applyUserMutation,
    markTurnStart,
    markTurnEnd,
    applyAgentPatch,
    resolveConflict,
    dismissPendingPatch,
  };
}
